import {Devices} from '@src/components/bronze/devices/devices';
import {Domains} from '@src/components/bronze/domains/domains';
import {IpList} from '@src/components/bronze/ip-list/ip-list';
import {BronzeCategories} from '@src/components/bronze/categories/bronze-categories';
import Alert from '@src/components/shared/alert/alert';
import {INTERVAL_POLLING} from '@src/utils/constants';
import {BronzeTabName, EventType} from '@src/dtos/analytics.dto';
import {BronzeVersionState, CircuitType, MainPages, VersionState, VersionStatus} from '@src/utils/enums';
import {isVersionPending} from '@src/utils/versionUtils';
import {Tabs} from 'antd';
import {observer} from 'mobx-react-lite';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {QuitEditingModal} from '../shared/quit-editing-modal/quit-editing-modal';
import {useStores} from '../store';
import {Reports} from '@src/components/bronze/reports/reports';
import IpIcon from '@svg/ip-control.svg?react';
import DevicesIcon from '@svg/devices.svg?react';
import DomainsIcon from '@svg/domains.svg?react';
import CategoriesIcon from '@svg/categories.svg?react';
import ReportsIcon from '@svg/reports-and-logs.svg?react';

interface DashboardBronzeContentProps {
    onTabChange?: (key: string) => void;
}

export const DashboardBronzeContent: React.FC<DashboardBronzeContentProps> = observer(({onTabChange}) => {

    // Holds the active polling interval across renders
    const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

    const {fwVersionBronzeStore, customerStore, uiStore} = useStores();

    const isMobileCircuit = customerStore.selectedLocation?.circuitType === CircuitType.MOBILE_CRU;

    const AbfTabs = [
        ...(isMobileCircuit ? [{
            key: 'devices',
            label: <span className="tab-label"><DevicesIcon/>Devices</span>,
            element: <Devices/>
        }] : [{key: 'ip', label: <span className="tab-label"><IpIcon/>IP Exceptions</span>, element: <IpList/>}]),
        {key: 'domains', label: <span className="tab-label"><DomainsIcon/>Domains</span>, element: <Domains/>},
        {key: 'categories', label: <span className="tab-label"><CategoriesIcon/>Categories</span>, element: <BronzeCategories/>},
        {key: 'reports', label: <span className="tab-label"><ReportsIcon/>Reports</span>, element: <Reports/>},
    ];

    const bronzeTabNameMap: Record<string, BronzeTabName> = {
        'devices': BronzeTabName.DEVICES,
        'ip': BronzeTabName.IP_EXCEPTIONS,
        'domains': BronzeTabName.DOMAINS,
        'categories': BronzeTabName.CATEGORIES,
        'reports': BronzeTabName.REPORTS,
    };

    // Removed local applyPolicyTime, now using store
    const [abfTabKey, setAbfTabKey] = useState(AbfTabs[0].key);

    useEffect(() => {
        onTabChange?.(abfTabKey);
    }, [abfTabKey]);
    const [pendingKey, setPendingKey] = useState<string | null>(null);
    const [isQuitEditingModalOpen, setIsQuitEditingModalOpen] = useState(false);

    useEffect(() => {
        if (!customerStore.hasBronzeDnsFeatures || !customerStore.selectedLocation) return;
        const bronzeTabName = bronzeTabNameMap[abfTabKey];
        if (bronzeTabName) {
            if (abfTabKey === 'devices') {
                uiStore.sendGeneralEvent(EventType.PageView, { pageName: MainPages.BRONZE, tabName: bronzeTabName, location: 'mobilityConnectionPage' });
            } else {
                uiStore.sendPageViewEvent(MainPages.BRONZE, bronzeTabName);
            }
        }
    }, [abfTabKey, customerStore.hasBronzeDnsFeatures, customerStore.selectedLocation]);

    // Stable callbacks to satisfy hook lint rules and avoid re-creating intervals unnecessarily
    const clearPolling = useCallback(() => {
        if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
        }
    }, []);

    const startPolling = useCallback((versionNumber: string, versionState: VersionState) => {
        clearPolling();
        intervalIdRef.current = setInterval(() => {
            void fwVersionBronzeStore.fetchLatestVersion(versionNumber, versionState);
        }, INTERVAL_POLLING);
    }, [clearPolling, fwVersionBronzeStore]);

    // For ABF customers, initialize button in APPLY (visible + disabled) state when version is loaded
    useEffect(() => {
        if (customerStore.hasBronzeDnsFeatures && fwVersionBronzeStore.selectedFwVersion && !fwVersionBronzeStore.versionState) {
            fwVersionBronzeStore.setVersionState(BronzeVersionState.APPLY);
        }
    }, [fwVersionBronzeStore.selectedFwVersion, customerStore.hasBronzeDnsFeatures, fwVersionBronzeStore]);

    useEffect(() => {
        const { selectedFwVersion } = fwVersionBronzeStore;

        if (!selectedFwVersion) return;

        if (selectedFwVersion.versionState === VersionState.ACTIVE) {
            clearPolling();
            return;
        }

        if (isVersionPending(selectedFwVersion)) {
            fwVersionBronzeStore.setVersionState(BronzeVersionState.PENDING);
            const { externalId, versionState } = selectedFwVersion;
            startPolling(externalId, versionState);
            return;
        }

        if (selectedFwVersion.versionStatus === VersionStatus.PUSH_ERROR) {
            void fwVersionBronzeStore.fetchLatestVersion(selectedFwVersion.externalId, selectedFwVersion.versionState);
        }
    }, [fwVersionBronzeStore.selectedFwVersion, clearPolling, startPolling]);

    // Cleanup on unmount
    useEffect(() => () => clearPolling(), []);

    // Handler for tab change with unsaved changes confirmation
    const handleTabChange = (key: string) => {
        const categoryDomainTabs = new Set(['categories', 'domains']);
        const isCategoryDomainSwitch = categoryDomainTabs.has(abfTabKey) && categoryDomainTabs.has(key);
        if (isCategoryDomainSwitch) {
            setAbfTabKey(key);
        } else if (fwVersionBronzeStore.isDirty) {
            setIsQuitEditingModalOpen(true);
            setPendingKey(key);
        } else {
            setAbfTabKey(key);
        }
    };

    return (
        <div className={'dashboard-bronze-content'}>
            <div className={'dashboard-bronze-abf-content'}>
                <div className={'dashboard-bronze-general-header'}>
                    {fwVersionBronzeStore.displayError && <Alert message={'Apply policy failed! Please try again'} isError={true}></Alert>}
                    <div className={`apply-button-wrapper ${customerStore.selectedLocation?.circuitType?.toLowerCase()}`}>
                        {customerStore.hasBronzeDnsFeatures ? <Tabs
                            activeKey={abfTabKey}
                            onChange={handleTabChange}
                            items={AbfTabs.map(item => item)}
                        /> : <div className={'title'}>IP Exceptions</div>}
                    </div>
                </div>
                <div className={'dashboard-bronze-abf-tab-content'}>
                    {customerStore.hasBronzeDnsFeatures
                        ? AbfTabs.find(item => item.key === abfTabKey)?.element
                        : <IpList/>}
                </div>
            </div>
            {isQuitEditingModalOpen && <QuitEditingModal onDiscaredChanges={() => {
                setAbfTabKey(pendingKey ?? AbfTabs[0].key);
                setPendingKey(null);
                fwVersionBronzeStore.resetFwVersion();
                fwVersionBronzeStore.setVersionState(BronzeVersionState.APPLY);
                setIsQuitEditingModalOpen(false);
            }}
                                                         onStayEdit={() => setIsQuitEditingModalOpen(false)}/>}
        </div>
    );
});
